import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePost } from '../../../hooks/useApi';
import { useGet as useGetLocalSchedule } from '../../../hooks/localSchedule/useGet';
import { useClear as useClearLocalSchedule } from '../../../hooks/localSchedule/useClear';
import { useCancelRegistration } from '../../../hooks/useCancelRegistration';
import { saveCheckoutSummary } from '../../../utils/checkoutSummary';

// Queries whose data changes when a registration is created or cancelled:
// enrollment state, seat availability and the enrollment block.
const REGISTRATION_QUERY_KEYS = [
  ['/v1/auth/reg/students'],
  ['/v1/auth/reg/is-enrollment-blocked'],
  ['/v1/public/reg/schedules'],
  ['studentEnrollmentStatus'],
];

interface RegisterData {
  data?: {
    total?: number;
    paymentIntent?:
      | {
          id?: string;
          client_secret?: string;
        }
      | string;
    metadata?: {
      requiresPayment?: boolean;
      isBilledToOrganization?: boolean;
    };
    studentId?: number;
    studentRegistrationWindowId?: number;
    studentsSchedules?: Array<{
      currentlySchedulingId?: string;
    }>;
    createdStudentRegistrationWindows?: Array<{ id?: number }>;
  };
  isLoading: boolean;
  isError: boolean;
  error?: any;
}

type ScheduleSelections = any[];

export const useFinalizePage = () => {
  const params = useParams();
  const navigate = useNavigate();

  // Determine initial step based on URL parameters
  const getInitialStep = () => {
    if (params.currentlySchedulingId === 'complete') return 4;
    if (params.currentlySchedulingId && params.paymentIntentId) return 3; // Payment done, go to sign documentation
    if (params.currentlySchedulingId && !params.paymentIntentId) return 2; // Pending enrollment, go to payment
    return 0; // New enrollment, start from policies
  };

  const [step, setStep] = useState<number>(getInitialStep());
  const [error, setError] = useState<string | null>(null);

  const localSchedule = useGetLocalSchedule(true);
  const clearLocalSchedule = useClearLocalSchedule();

  const register = usePost<RegisterData, ScheduleSelections>(
    '/v1/auth/reg/register-schedule/register',
    { timeout: 90000, invalidates: REGISTRATION_QUERY_KEYS }
  );

  const cancelRegistration = useCancelRegistration();

  // Check if there are valid selections when the component mounts
  useEffect(() => {
    if (!params.currentlySchedulingId && !params.paymentIntentId) {
      if (
        !localSchedule ||
        !Array.isArray(localSchedule.selections) ||
        localSchedule.selections.length === 0
      ) {
        setError(
          'No valid schedule selections found. Please select schedules before proceeding to checkout.'
        );
      } else {
        setError(null);
      }
    }
  }, [localSchedule, params.currentlySchedulingId, params.paymentIntentId]);

  const onConfirmSchedule = () => {
    setStep(2);

    if (
      localSchedule &&
      Array.isArray(localSchedule.selections) &&
      localSchedule.selections.length > 0
    ) {
      const enhancedSelections = localSchedule.selections.map(
        (selection: any) => ({
          ...selection,
          frontendCalculatedData: {
            scheduleData: [
              {
                scheduleId: selection.scheduleId,
                fee: selection.fee || 0,
                totalDays: selection.totalDays || 0,
                monday: selection.monday || false,
                tuesday: selection.tuesday || false,
                wednesday: selection.wednesday || false,
                thursday: selection.thursday || false,
                friday: selection.friday || false,
              },
            ],
            // Grade id for the program year; see WeekSelector.
            studentGrade: selection.student?.promotedGrade,
            shouldPromote: selection.student?.shouldPromote || false,
          },
        })
      );

      register.mutate(enhancedSelections, {
        onSuccess: (data) => {
          // The hook returns the API's `return` payload, not RegisterData.
          const result = data as unknown as RegisterData['data'];
          const paymentIntent = result?.paymentIntent;
          const paymentIntentId =
            typeof paymentIntent === 'string'
              ? paymentIntent
              : paymentIntent?.id;
          if (paymentIntentId) {
            saveCheckoutSummary(paymentIntentId, localSchedule.selections);
          }

          // Put the registration in the URL. A refresh or a return visit
          // then resumes payment for it instead of registering again.
          const currentlySchedulingId =
            result?.studentsSchedules?.[0]?.currentlySchedulingId;
          if (currentlySchedulingId) {
            navigate(`/finalize/${currentlySchedulingId}`, { replace: true });
          }
        },
      });
    } else {
      console.error('No valid schedule selections found');
      setError(
        'No valid schedule selections found. Please select schedules before proceeding to checkout.'
      );
      navigate('/scheduling');
    }
  };

  const registered = register.data as RegisterData['data'];
  const registerData: RegisterData = {
    data: registered && {
      ...registered,
      studentRegistrationWindowId:
        registered.createdStudentRegistrationWindows?.[0]?.id,
    },
    isLoading: register.isLoading,
    isError: register.isError,
    error: register.error,
  };

  const onCancelRegistration = async () => {
    const registrationId = registerData.data?.studentRegistrationWindowId;
    const hasRegistrationId =
      Number.isSafeInteger(registrationId) && Number(registrationId) > 0;

    if (hasRegistrationId) {
      try {
        await cancelRegistration.mutateAsync(String(registrationId));
        clearLocalSchedule();
        navigate('/scheduling');
      } catch (error) {
        console.error('Failed to cancel registration:', error);
        setError('Failed to cancel registration. Please try again.');
      }
    } else if (registerData.data || params.currentlySchedulingId) {
      setError(
        'Unable to cancel registration. Reload this registration before trying again.'
      );
    } else {
      // Canceling checkout before registration only clears the local cart.
      clearLocalSchedule();
      navigate('/scheduling');
    }
  };

  return {
    step,
    setStep,
    error,
    registerData,
    cancelRegistration,
    onConfirmSchedule,
    onCancelRegistration,
  };
};
